// Per-framework integration snippets + guides for the affiliate onboarding wizard.
// Rendered by PlatformSnippetTabs as a flat platform list (HTML | WordPress |
// Shopify | Wix | HubSpot | Next.js | React Native | Flutter | Node.js | PHP | REST API),
// the way Segment/Stripe/GA docs do it.
//
// NOTE: none of these need backend changes — every web platform installs the same
// <head> pixel, every server platform calls the same POST /pixel/event. Only the
// instructions differ.

import { SnippetDef, SnippetFile, SnippetOption } from '../components/PlatformSnippetTabs';

const EVENT_API_URL = 'https://api.clanconnect.ai/pixel/event';

interface PixelKeys {
    cctm_id?: string;
    publishable_key?: string;
    secret_key?: string;
}

const sk = (pixel?: PixelKeys) => pixel?.secret_key || 'YOUR_SECRET_KEY';
const pk = (pixel?: PixelKeys) => pixel?.publishable_key || 'pk_live_...';
const cctm = (pixel?: PixelKeys) => pixel?.cctm_id || 'cctm-xxxxxxxx';

// =====================================================================
// Shared building blocks
// =====================================================================

/** The core <head> pixel loader — identical for every web platform. */
const pixelHtml = (pixel?: PixelKeys) => `<!-- ClanConnect Affiliate Pixel -->
<script>
  !function(w,d,s,l,i){
    w[l]=w[l]||[];
    var f=d.getElementsByTagName(s)[0],j=d.createElement(s);
    j.async=true;
    j.src='https://s3.ap-south-1.amazonaws.com/new-clanconnect.ai/affiliate/affiliate.js?id='+i;
    f.parentNode.insertBefore(j,f);
  }(window,document,'script','cctLayer','${cctm(pixel)}');
  window.cctLayer=window.cctLayer||[];
  function cctag(){cctLayer.push(arguments);}
  cctag('js',new Date());
  cctag('config','${cctm(pixel)}');
</script>
<!-- End ClanConnect Affiliate Pixel -->`;

/** Browser cctag() event call. */
const cctagEvent = (eventType: string, fields: string) => `<script>
  cctag('event', '${eventType}', {
${fields}
  });
</script>`;

/** React / Next.js event call (window.cctag from the installed pixel). */
const reactEvent = (eventType: string, fields: string) => `// In your component or event handler — window.cctag is the pixel from Step 1.
// (The global.d.ts declaration from the setup step makes TypeScript accept this.)
window.cctag?.('event', '${eventType}', {
${fields}
});`;

/**
 * The src/global.d.ts file shared by the React-family setup tabs
 * (React / Helmet / Next.js): the TypeScript Window declaration that lets
 * components call window.cctag(…). Rendered as its own code block.
 * The actual event calls live in the Add to Cart / Checkout / Purchase steps.
 */
const globalDtsFile: SnippetFile = {
    title: 'src/global.d.ts',
    code: `// Lets TypeScript know the pixel globals exist.
// Uses interface merging on Window — do NOT write "type Window = ...".
// With this in place, components can call window.cctag(…) for the events in
// the next steps (Add to Cart, Initiate Checkout, Track Purchase).
interface Window {
  cctLayer: any[];
  cctag: (...args: any[]) => void;
}`,
};

/** Node.js SDK event call. */
const nodeEvent = (pixel: PixelKeys | undefined, method: string, fields: string) => `// npm install @clanconnect/pixel-sdk
import { ClanConnect } from '@clanconnect/pixel-sdk';

// Once at startup — secret key from env, never hardcoded
ClanConnect.init({ cctmId: '${cctm(pixel)}', secretKey: process.env.CC_SECRET_KEY });

await ClanConnect.${method}({
${fields}
});`;

/** PHP server-to-server event. */
const phpEvent = (pixel: PixelKeys | undefined, eventType: string, dataLines: string, topLines: string = '') => `<?php
// Send from your backend — e.g. after your own cart/order logic
$payload = [
  'cctm_id'    => '${cctm(pixel)}',
  'event_type' => '${eventType}',${topLines}
  'u_id'       => $userId,          // your stable user/device id
  'cct_click_id' => $cctClickId ?? null, // from the affiliate link, if stored
  'event_data' => [
${dataLines}
  ],
];

$ch = curl_init('${EVENT_API_URL}');
curl_setopt_array($ch, [
  CURLOPT_POST           => true,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER     => [
    'Content-Type: application/json',
    'Authorization: Bearer ' . getenv('CC_SECRET_KEY'), // ${sk(pixel).substring(0, 12)}…
  ],
  CURLOPT_POSTFIELDS     => json_encode($payload),
]);
curl_exec($ch);
curl_close($ch);`;

/** Raw REST API curl. */
const curlEvent = (pixel: PixelKeys | undefined, eventType: string, eventDataJson: string, extra: string = '') => `curl -X POST ${EVENT_API_URL} \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer ${sk(pixel)}' \\
  -d '{
    "cctm_id": "${cctm(pixel)}",
    "event_type": "${eventType}",${extra}
    "u_id": "<stable-user-or-device-id>",
    "cct_click_id": "<from deep link, if you stored it>",
    "event_data": ${eventDataJson}
  }'`;

/** React Native SDK event call. */
const rnEvent = (method: string, fields: string) => `// ClanConnect.init() from Step 1 must already have run
import { ClanConnect } from '@clanconnect/pixel-sdk';

ClanConnect.${method}({
${fields}
});`;

/** Flutter SDK event call. */
const flutterEvent = (method: string, fields: string) => `// ClanConnect.init() from Step 1 must already have run
import 'package:clanconnect_pixel/clanconnect_pixel.dart';

await ClanConnect.${method}({
${fields}
});`;

const serverGuide = (when: string[]): SnippetDef['guide'] => ({
    title: 'When to send this from your backend',
    steps: [
        ...when,
        'Keep the secret key in an environment variable — never in client code.',
        'Use the same u_id across funnel events so they join up.',
    ],
});

// =====================================================================
// Step 1 — Setup / install options
// =====================================================================

export const setupOptions = (pixel?: PixelKeys): SnippetOption[] => [
    {
        id: 'html',
        label: 'HTML',
        group: 'web',
        snippet: {
            title: 'affiliate-pixel.html',
            code: pixelHtml(pixel),
            guide: {
                title: 'Any website — install the pixel',
                steps: [
                    'Copy the snippet on the left.',
                    'Paste it inside the <head> tag of every page — usually your global layout or base template, just before </head>.',
                    'Deploy. The pixel activates when a buyer arrives from a ClanConnect link (utm_source=clanconnect).',
                    'Verify: open your site with ?utm_source=clanconnect&utm_campaign=test and check the Network tab for a pixel/event request.',
                ],
                note: 'Install once — the event snippets in the next steps reuse this same cctag() function.',
            },
        },
    },
    {
        id: 'wordpress',
        label: 'WordPress',
        group: 'web',
        snippet: {
            title: 'functions.php',
            code: `// Option A (no code): install the free "WPCode" plugin →
// Code Snippets → Header & Footer → paste the HTML-tab snippet into "Header".

// Option B (child theme functions.php):
add_action('wp_head', function () { ?>
${pixelHtml(pixel)}
<?php }, 5);`,
            guide: {
                title: 'WordPress / WooCommerce',
                steps: [
                    'Easiest: install the WPCode (or similar header/footer) plugin and paste the HTML snippet into the site Header — no code needed.',
                    'Or add the wp_head action shown on the left to your child theme\'s functions.php.',
                    'Clear any page-cache plugin after saving.',
                    'WooCommerce stores: the event steps that follow include ready-made WooCommerce hooks.',
                ],
            },
        },
    },
    {
        id: 'shopify',
        label: 'Shopify',
        group: 'web',
        snippet: {
            title: 'theme.liquid',
            code: pixelHtml(pixel),
            guide: {
                title: 'Shopify',
                steps: [
                    'Admin → Online Store → Themes → ⋯ → Edit code.',
                    'Open layout/theme.liquid.',
                    'Paste the snippet just before </head> and save.',
                    'For purchase tracking, also see the Shopify tab in the Track Purchase step (order status page script).',
                ],
            },
        },
    },
    {
        id: 'wix',
        label: 'Wix',
        group: 'web',
        snippet: {
            title: 'wix-custom-code.html',
            code: pixelHtml(pixel),
            guide: {
                title: 'Wix',
                steps: [
                    'Wix Dashboard → Settings → Custom Code (under Advanced).',
                    'Click "+ Add Custom Code", paste the snippet.',
                    'Set "Add Code to Pages" = All pages, placement = Head, and apply.',
                    'Custom code requires a Wix premium plan with a connected domain.',
                ],
                note: 'Cart/checkout events on Wix need Velo (Wix\'s dev mode) — pageview attribution works with just this snippet.',
            },
        },
    },
    {
        id: 'hubspot',
        label: 'HubSpot',
        group: 'web',
        snippet: {
            title: 'hubspot-site-header.html',
            code: pixelHtml(pixel),
            guide: {
                title: 'HubSpot CMS',
                steps: [
                    'HubSpot → Settings → Website → Pages.',
                    'Choose the domain (or all domains) → "Site header HTML".',
                    'Paste the snippet and save — it now loads on every CMS page.',
                    'If your store runs elsewhere (e.g. Shopify) install the pixel there too.',
                ],
            },
        },
    },
    {
        id: 'react',
        label: 'React',
        group: 'web',
        snippet: {
            files: [
                {
                    title: 'public/index.html',
                    code: `<!-- inside <head>, just before </head> (Vite: index.html) -->
${pixelHtml(pixel)}`,
                },
                globalDtsFile,
            ],
            guide: {
                title: 'React (CRA / Vite SPA)',
                steps: [
                    'Open public/index.html (Create React App) or index.html (Vite).',
                    'Paste the pixel snippet inside <head>, just before </head>. The pixel loads once for the whole SPA and tracks route changes automatically (popstate / click listeners).',
                    'Add the window declaration to src/global.d.ts so TypeScript accepts window.cctag(…) calls from your .tsx files.',
                    'Fire events from any component or service with window.cctag?.(…) — see the next steps for add-to-cart, checkout and purchase payloads.',
                ],
                note: 'index.html loads the pixel before React boots, so no App.tsx wiring is needed. Can\'t edit index.html, or want the pixel only in production? Use the React (Helmet) tab. Using Next.js? Switch to the Next.js tab.',
            },
        },
    },
    {
        id: 'react-helmet',
        label: 'React (Helmet)',
        group: 'web',
        snippet: {
            files: [
                {
                    title: 'Terminal',
                    code: 'npm install react-helmet @types/react-helmet --legacy-peer-deps',
                },
                {
                    title: 'src/ClanConnectPixel.tsx',
                    code: `// The pixel — react-helmet injects it into <head> at runtime.
import React from 'react';
import { Helmet } from 'react-helmet';

const ClanConnectPixel: React.FC = () => (
  <Helmet>
    <script>
      {\`
        !function(w,d,s,l,i){
          w[l]=w[l]||[];
          var f=d.getElementsByTagName(s)[0],j=d.createElement(s);
          j.async=true;
          j.src='https://s3.ap-south-1.amazonaws.com/new-clanconnect.ai/affiliate/affiliate.js?id='+i;
          f.parentNode.insertBefore(j,f);
        }(window,document,'script','cctLayer','${cctm(pixel)}');
        window.cctLayer=window.cctLayer||[];
        window.cctag=function(){cctLayer.push(arguments);};
        cctag('js',new Date());
        cctag('config','${cctm(pixel)}');
      \`}
    </script>
  </Helmet>
);

export default ClanConnectPixel;`,
                },
                {
                    title: 'src/App.tsx',
                    code: `// Mount ONCE at the root, before your routes.
import React from 'react';
import ClanConnectPixel from './ClanConnectPixel';

const App: React.FC = () => (
  <>
    {/* Guarded so local/staging traffic never pollutes your analytics */}
    {process.env.NODE_ENV === 'production' && <ClanConnectPixel />}

    {/* …your routes / app content… */}
  </>
);

export default App;`,
                },
                globalDtsFile,
            ],
            guide: {
                title: 'React with react-helmet',
                steps: [
                    'Install react-helmet and its types.',
                    'Create src/ClanConnectPixel.tsx with the component above — react-helmet injects the pixel <script> into <head> at runtime.',
                    'Mount <ClanConnectPixel /> once at the root of App.tsx, wrapped in a production guard so local and staging sessions stay out of your analytics.',
                    'Add the window declaration to src/global.d.ts (interface merging on Window) so TypeScript accepts window.cctag(…) calls.',
                    'Fire events from any component or service with window.cctag?.(…) — see the next steps for add-to-cart, checkout and purchase payloads.',
                ],
                note: 'Choose this over the React (index.html) tab when you can\'t edit index.html, or want env-based control over when the pixel loads. The index.html approach loads slightly earlier; both end up identical once the page is interactive.',
            },
        },
    },
    {
        id: 'nextjs',
        label: 'Next.js',
        group: 'web',
        snippet: {
            files: [
                {
                    title: 'app/layout.tsx',
                    code: `import Script from 'next/script';

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <Script id="cct-pixel" strategy="afterInteractive">{\`
          !function(w,d,s,l,i){
            w[l]=w[l]||[];
            var f=d.getElementsByTagName(s)[0],j=d.createElement(s);
            j.async=true;
            j.src='https://s3.ap-south-1.amazonaws.com/new-clanconnect.ai/affiliate/affiliate.js?id='+i;
            f.parentNode.insertBefore(j,f);
          }(window,document,'script','cctLayer','${cctm(pixel)}');
          window.cctLayer=window.cctLayer||[];
          window.cctag=function(){cctLayer.push(arguments);};
          cctag('js',new Date());
          cctag('config','${cctm(pixel)}');
        \`}</Script>
      </head>
      <body>{children}</body>
    </html>
  );
}`,
                },
                globalDtsFile,
            ],
            guide: {
                title: 'Next.js',
                steps: [
                    'Add the <Script> to your root layout (App Router) or _app/_document (Pages Router) — it loads the pixel once for the whole app.',
                    'strategy="afterInteractive" keeps it off the critical path.',
                    'Add the window declaration to src/global.d.ts so TypeScript accepts window.cctag(…) calls from your components.',
                    'Fire events from any client component or handler with window.cctag?.(…) — see the next steps for add-to-cart, checkout and purchase payloads.',
                    'Plain React without Next.js? Use the React tab instead.',
                ],
                note: 'window.cctag only exists in the browser — call it from client components or event handlers, never during server rendering.',
            },
        },
    },
    {
        id: 'react-native',
        label: 'React Native',
        group: 'mobile',
        snippet: {
            title: 'App.tsx',
            code: `// npm install @clanconnect/pixel-sdk @react-native-async-storage/async-storage
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ClanConnect } from '@clanconnect/pixel-sdk';
import { Linking } from 'react-native';

ClanConnect.init({
  cctmId: '${cctm(pixel)}',
  publishableKey: '${pk(pixel)}',
  storage: AsyncStorage,
});

// Capture affiliate deep links (attribution):
Linking.getInitialURL().then((url) => url && ClanConnect.handleDeepLink(url));
Linking.addEventListener('url', ({ url }) => ClanConnect.handleDeepLink(url));`,
            guide: {
                title: 'React Native SDK',
                steps: [
                    'Install @clanconnect/pixel-sdk and AsyncStorage.',
                    'Call ClanConnect.init() once at app startup.',
                    'Wire your deep link handler to ClanConnect.handleDeepLink(url) — this attributes the buyer to the influencer link they tapped.',
                    'Track funnel events with the SDK calls in the next steps.',
                ],
                note: 'Only the publishable key (pk_live_…) goes in the app. Never ship the secret key in client code.',
            },
        },
    },
    {
        id: 'flutter',
        label: 'Flutter',
        group: 'mobile',
        snippet: {
            title: 'main.dart',
            code: `// pubspec.yaml → dependencies: clanconnect_pixel: ^0.1.0
import 'package:clanconnect_pixel/clanconnect_pixel.dart';

Future<void> main() async {
  WidgetsFlutterBinding.ensureInitialized();

  await ClanConnect.init(
    cctmId: '${cctm(pixel)}',
    publishableKey: '${pk(pixel)}',
  );

  runApp(const MyApp());
}

// In your deep link handler (app_links / uni_links):
// await ClanConnect.handleDeepLink(uri.toString());`,
            guide: {
                title: 'Flutter SDK',
                steps: [
                    'Add clanconnect_pixel to pubspec.yaml and run flutter pub get.',
                    'Call ClanConnect.init() before runApp().',
                    'Pass incoming deep links to ClanConnect.handleDeepLink() — this attributes the buyer to the influencer link.',
                    'Track funnel events with the SDK calls in the next steps.',
                ],
                note: 'Only the publishable key (pk_live_…) goes in the app. Never ship the secret key in client code.',
            },
        },
    },
    {
        id: 'nodejs',
        label: 'Node.js',
        group: 'server',
        snippet: {
            title: 'clanconnect.ts',
            code: `// npm install @clanconnect/pixel-sdk
import { ClanConnect } from '@clanconnect/pixel-sdk';

// Once at server startup — secret key from env
ClanConnect.init({
  cctmId: '${cctm(pixel)}',
  secretKey: process.env.CC_SECRET_KEY, // ${sk(pixel).substring(0, 12)}…
});`,
            guide: {
                title: 'Node.js (server-to-server)',
                steps: [
                    'Install the same @clanconnect/pixel-sdk package — with secretKey it sends as a trusted server.',
                    'Put the secret key in an environment variable.',
                    'Call the event methods from your API routes / order webhooks (next steps).',
                    'Pass cct_click_id through from your client when available.',
                ],
            },
        },
    },
    {
        id: 'php',
        label: 'PHP',
        group: 'server',
        snippet: {
            title: 'clanconnect.php',
            code: phpEvent(pixel, 'pageview', `    // event-specific fields go here (see next steps)`),
            guide: {
                title: 'PHP (server-to-server)',
                steps: [
                    'Works with any framework — Laravel, CodeIgniter, plain PHP.',
                    'Set CC_SECRET_KEY in your environment (.env / server config).',
                    'Wrap the curl call in a helper and call it from your cart/order code (next steps).',
                    'The endpoint always returns HTTP 200 — fire-and-forget is safe.',
                ],
            },
        },
    },
    {
        id: 'api',
        label: 'REST API',
        group: 'server',
        snippet: {
            title: 'events-api.sh',
            code: curlEvent(pixel, 'pageview', `{}`),
            guide: {
                title: 'Any language — raw Events API',
                steps: [
                    'One endpoint: POST /pixel/event with a JSON body.',
                    'Authenticate with Authorization: Bearer <secret key> from your server.',
                    'Event types: pageview, add_to_cart, initiate_checkout, purchase, custom.',
                    'Use event_id (your order id) on purchases to dedup against client-side reports.',
                ],
                note: 'Invalid keys are dropped silently with HTTP 200 — tracking can never break your checkout.',
            },
        },
    },
];

// =====================================================================
// Steps 2–4 — Event options
// =====================================================================

interface EventSpec {
    /** cctag/Window JS fields */
    jsFields: string;
    /** SDK (RN / Node) fields */
    sdkFields: string;
    /** Flutter map fields */
    dartFields: string;
    /** PHP event_data lines */
    phpData: string;
    phpTop?: string;
    /** curl event_data JSON */
    curlData: string;
    curlExtra?: string;
    /** WooCommerce PHP hook snippet */
    wooCode: string;
    /** Shopify snippet */
    shopifyCode: string;
    shopifyGuide: string[];
    /** Per-platform "where to put it" guidance */
    webWhere: string[];
    appWhere: string[];
    serverWhen: string[];
    webNote?: string;
}

const buildEventOptions = (
    pixel: PixelKeys | undefined,
    eventType: string,
    sdkMethod: string,
    spec: EventSpec
): SnippetOption[] => [
    {
        id: 'html',
        label: 'HTML / JS',
        group: 'web',
        snippet: {
            title: `${eventType}.html`,
            code: cctagEvent(eventType, spec.jsFields),
            guide: { title: 'Where to add this on your website', steps: spec.webWhere, note: spec.webNote },
        },
    },
    {
        id: 'wordpress',
        label: 'WordPress',
        group: 'web',
        snippet: {
            title: 'functions.php (WooCommerce)',
            code: spec.wooCode,
            guide: {
                title: 'WooCommerce hook',
                steps: [
                    'Add to your child theme\'s functions.php (or a WPCode PHP snippet).',
                    'The hook injects the cctag() call at the right moment automatically.',
                    'Requires the pixel from Step 1 to be installed.',
                ],
            },
        },
    },
    {
        id: 'shopify',
        label: 'Shopify',
        group: 'web',
        snippet: {
            title: `${eventType}.liquid`,
            code: spec.shopifyCode,
            guide: { title: 'Shopify', steps: spec.shopifyGuide },
        },
    },
    {
        id: 'wix',
        label: 'Wix',
        group: 'web',
        snippet: {
            title: `${eventType}.html`,
            code: cctagEvent(eventType, spec.jsFields),
            guide: {
                title: 'Wix',
                steps: [
                    'With the pixel installed (Step 1), cctag() is available site-wide.',
                    'Fire it from a Velo event handler (e.g. a button onClick) or a page-level custom code block.',
                    ...spec.webWhere,
                ],
                note: 'Cart/checkout events on Wix generally need Velo (Wix\'s dev mode) — see the Step 1 Wix note.',
            },
        },
    },
    {
        id: 'hubspot',
        label: 'HubSpot',
        group: 'web',
        snippet: {
            title: `${eventType}.html`,
            code: cctagEvent(eventType, spec.jsFields),
            guide: {
                title: 'HubSpot CMS',
                steps: [
                    'With the pixel installed (Step 1), cctag() is available on every CMS page.',
                    'Embed this in a custom module or the page HTML where the action happens.',
                    ...spec.webWhere,
                ],
                note: 'If your store runs elsewhere (e.g. Shopify), fire the event there instead.',
            },
        },
    },
    {
        id: 'react',
        label: 'React',
        group: 'web',
        snippet: {
            title: `${sdkMethod}.tsx`,
            code: reactEvent(eventType, spec.jsFields),
            guide: { title: 'Where to call this in your component', steps: spec.webWhere },
        },
    },
    {
        id: 'react-helmet',
        label: 'React (Helmet)',
        group: 'web',
        snippet: {
            title: `${sdkMethod}.tsx`,
            code: reactEvent(eventType, spec.jsFields),
            guide: {
                title: 'Where to call this in your component',
                steps: spec.webWhere,
                note: 'Event firing is identical however you installed the pixel — react-helmet only affects setup (Step 1).',
            },
        },
    },
    {
        id: 'nextjs',
        label: 'Next.js',
        group: 'web',
        snippet: {
            title: `${sdkMethod}.tsx`,
            code: reactEvent(eventType, spec.jsFields),
            guide: {
                title: 'Where to call this in your component',
                steps: spec.webWhere,
                note: 'Call from a client component or event handler — window.cctag only exists in the browser, never during server rendering.',
            },
        },
    },
    {
        id: 'react-native',
        label: 'React Native',
        group: 'mobile',
        snippet: {
            title: `${sdkMethod}.ts`,
            code: rnEvent(sdkMethod, spec.sdkFields),
            guide: { title: 'Where to call this in your app', steps: spec.appWhere },
        },
    },
    {
        id: 'flutter',
        label: 'Flutter',
        group: 'mobile',
        snippet: {
            title: `${sdkMethod}.dart`,
            code: flutterEvent(sdkMethod, spec.dartFields),
            guide: { title: 'Where to call this in your app', steps: spec.appWhere },
        },
    },
    {
        id: 'nodejs',
        label: 'Node.js',
        group: 'server',
        snippet: {
            title: `${sdkMethod}.ts`,
            code: nodeEvent(pixel, sdkMethod, spec.sdkFields),
            guide: serverGuide(spec.serverWhen),
        },
    },
    {
        id: 'php',
        label: 'PHP',
        group: 'server',
        snippet: {
            title: `${sdkMethod}.php`,
            code: phpEvent(pixel, eventType, spec.phpData, spec.phpTop),
            guide: serverGuide(spec.serverWhen),
        },
    },
    {
        id: 'api',
        label: 'REST API',
        group: 'server',
        snippet: {
            title: `${eventType}.sh`,
            code: curlEvent(pixel, eventType, spec.curlData, spec.curlExtra),
            guide: serverGuide(spec.serverWhen),
        },
    },
];

// --- Add to Cart ---

export const addToCartOptions = (pixel?: PixelKeys): SnippetOption[] =>
    buildEventOptions(pixel, 'add_to_cart', 'addToCart', {
        jsFields: `    currency: 'INR',
    value: 499.00,
    items: [{
      item_id: 'YOUR-SKU-001',
      item_name: 'Product Name',
      price: 499.00,
      quantity: 1
    }]`,
        sdkFields: `  currency: 'INR',
  value: 499.00,
  items: [{ item_id: 'YOUR-SKU-001', item_name: 'Product Name', price: 499.00, quantity: 1 }]`,
        dartFields: `  'currency': 'INR',
  'value': 499.00,
  'items': [{'item_id': 'YOUR-SKU-001', 'item_name': 'Product Name', 'price': 499.00, 'quantity': 1}]`,
        phpData: `    'currency' => 'INR',
    'value'    => 499.00,
    'items'    => [['item_id' => 'YOUR-SKU-001', 'price' => 499.00, 'quantity' => 1]],`,
        curlData: `{
      "currency": "INR",
      "value": 499.00,
      "items": [{"item_id": "YOUR-SKU-001", "item_name": "Product Name", "price": 499.00, "quantity": 1}]
    }`,
        wooCode: `add_action('woocommerce_add_to_cart', function ($cart_item_key, $product_id, $quantity) {
    $product = wc_get_product($product_id); ?>
    <script>
      cctag('event', 'add_to_cart', {
        currency: '<?php echo esc_js(get_woocommerce_currency()); ?>',
        value: <?php echo (float) $product->get_price() * $quantity; ?>,
        items: [{
          item_id: '<?php echo esc_js($product->get_sku() ?: $product_id); ?>',
          item_name: '<?php echo esc_js($product->get_name()); ?>',
          price: <?php echo (float) $product->get_price(); ?>,
          quantity: <?php echo (int) $quantity; ?>
        }]
      });
    </script>
<?php }, 10, 3);`,
        shopifyCode: `<!-- In your theme's product form JS (e.g. assets/theme.js) -->
document.querySelectorAll('form[action="/cart/add"]').forEach(function (form) {
  form.addEventListener('submit', function () {
    cctag('event', 'add_to_cart', {
      currency: Shopify.currency.active,
      value: Number(form.dataset.price || 0),
      items: [{ item_id: form.querySelector('[name="id"]').value, quantity: 1 }]
    });
  });
});`,
        shopifyGuide: [
            'Add to your theme JS (Online Store → Edit code → assets).',
            'Hooks every "Add to cart" form submit.',
            'Requires the pixel from Step 1 in theme.liquid.',
        ],
        webWhere: [
            'Place inside your "Add to Cart" button\'s click handler, after your own cart logic succeeds.',
            'Replace value and items with the real product being added.',
            'Fire once per add-to-cart action.',
        ],
        webNote: 'Requires the pixel from Step 1 — it defines cctag().',
        appWhere: [
            'Call from your add-to-cart action (button handler, store action or view model).',
            'Pass the real product data — the SDK attaches visitor id, session and attribution automatically.',
            'The SDK queues and retries automatically if the device is offline.',
        ],
        serverWhen: ['Send when your backend records a cart addition (e.g. your /cart API).'],
    });

// --- Initiate Checkout ---

export const checkoutOptions = (pixel?: PixelKeys): SnippetOption[] =>
    buildEventOptions(pixel, 'initiate_checkout', 'initiateCheckout', {
        jsFields: `    currency: 'INR',
    value: 499.00,
    num_items: 1`,
        sdkFields: `  currency: 'INR',
  value: 499.00,
  num_items: 1`,
        dartFields: `  'currency': 'INR',
  'value': 499.00,
  'num_items': 1`,
        phpData: `    'currency'  => 'INR',
    'value'     => 499.00,
    'num_items' => 1,`,
        curlData: `{
      "currency": "INR",
      "value": 499.00,
      "num_items": 1
    }`,
        wooCode: `add_action('woocommerce_before_checkout_form', function () { ?>
    <script>
      cctag('event', 'initiate_checkout', {
        currency: '<?php echo esc_js(get_woocommerce_currency()); ?>',
        value: <?php echo (float) WC()->cart->get_total('edit'); ?>,
        num_items: <?php echo (int) WC()->cart->get_cart_contents_count(); ?>
      });
    </script>
<?php });`,
        shopifyCode: `<!-- layout/theme.liquid — fires on the cart page -->
{% if template == 'cart' %}
<script>
  cctag('event', 'initiate_checkout', {
    currency: '{{ cart.currency.iso_code }}',
    value: {{ cart.total_price | divided_by: 100.0 }},
    num_items: {{ cart.item_count }}
  });
</script>
{% endif %}`,
        shopifyGuide: [
            'Shopify locks the checkout itself — fire on the cart page instead.',
            'Paste into theme.liquid (the template == \'cart\' guard limits it).',
            'Requires the pixel from Step 1.',
        ],
        webWhere: [
            'Fire on your checkout page load, or when the customer clicks "Proceed to Checkout".',
            'Pass the full cart value and item count at that moment.',
            'Fire once per checkout attempt, not on every render.',
        ],
        appWhere: [
            'Call when the checkout screen opens (or the "Checkout" button is tapped).',
            'Pass the cart total and item count at that moment.',
        ],
        serverWhen: ['Send when your backend creates a checkout / draft order.'],
    });

// --- Purchase ---

export const purchaseOptions = (pixel?: PixelKeys): SnippetOption[] =>
    buildEventOptions(pixel, 'purchase', 'purchase', {
        jsFields: `    transaction_id: 'YOUR-ORDER-ID',
    currency: 'INR',
    value: 499.00,
    items: [{
      item_id: 'YOUR-SKU-001',
      item_name: 'Product Name',
      price: 499.00,
      quantity: 1
    }]`,
        sdkFields: `  transaction_id: 'YOUR-ORDER-ID',
  event_id: 'YOUR-ORDER-ID', // dedups if your server also reports it
  currency: 'INR',
  value: 499.00`,
        dartFields: `  'transaction_id': 'YOUR-ORDER-ID',
  'event_id': 'YOUR-ORDER-ID',
  'currency': 'INR',
  'value': 499.00`,
        phpTop: `\n  'event_id'   => $orderId, // dedups against client-side reports`,
        phpData: `    'transaction_id' => $orderId,
    'currency'       => 'INR',
    'value'          => $orderTotal,
    'items'          => $orderItems,`,
        curlData: `{
      "transaction_id": "YOUR-ORDER-ID",
      "currency": "INR",
      "value": 499.00,
      "items": [{"item_id": "YOUR-SKU-001", "price": 499.00, "quantity": 1}]
    }`,
        curlExtra: `\n    "event_id": "YOUR-ORDER-ID",`,
        wooCode: `add_action('woocommerce_thankyou', function ($order_id) {
    $order = wc_get_order($order_id);
    if (!$order) return; ?>
    <script>
      cctag('event', 'purchase', {
        transaction_id: '<?php echo esc_js($order->get_order_number()); ?>',
        currency: '<?php echo esc_js($order->get_currency()); ?>',
        value: <?php echo (float) $order->get_total(); ?>
      });
    </script>
<?php });`,
        shopifyCode: `<!-- Settings → Checkout → Order status page → Additional scripts -->
${pixelHtml(pixel)}
<script>
  cctag('event', 'purchase', {
    transaction_id: '{{ order_number }}',
    currency: '{{ currency }}',
    value: {{ total_price | divided_by: 100.0 }}
  });
</script>`,
        shopifyGuide: [
            'Settings → Checkout → Order status page → "Additional scripts".',
            'The pixel loader is included because the order page doesn\'t use your theme layout.',
            'Shopify dedupes additional scripts per order automatically.',
        ],
        webWhere: [
            'Place on your order confirmation / thank-you page.',
            'transaction_id must be your real, unique order id — it prevents double counting.',
            'Pass the final paid amount in value.',
        ],
        webNote:
            'Tip: also send the same purchase from your backend (Node.js / PHP / REST API tab) with event_id = order id — server confirmation is more reliable than a thank-you page that may not load.',
        appWhere: [
            'Call on your order-success screen after payment confirmation.',
            'Set both transaction_id and event_id to your order id.',
            'A matching event_id from your backend makes sure the sale counts once.',
        ],
        serverWhen: [
            'Send after your payment gateway confirms the charge (webhook / capture callback).',
            'Set event_id = order id so app/web reports of the same sale dedupe.',
        ],
    });

// =====================================================================
// Back-compat names used by Step1 before the framework-list refactor.
// (Setup tab content now comes from setupOptions().)
// =====================================================================

export const webPixelSnippet = (pixel?: PixelKeys): SnippetDef => setupOptions(pixel)[0].snippet;
export const sdkSetupSnippet = (pixel?: PixelKeys): SnippetDef =>
    setupOptions(pixel).find((o) => o.id === 'react-native')!.snippet;
export const serverSetupSnippet = (pixel?: PixelKeys): SnippetDef =>
    setupOptions(pixel).find((o) => o.id === 'api')!.snippet;
